import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 p-3 rounded-lg hover:bg-background-200/50 transition-colors cursor-pointer"
      >
        <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">{article.publishDate}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <h3 className="text-lg font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 mt-2 line-clamp-2">{article.summary}</p>
          <div className="flex items-center gap-3 mt-4 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span>&middot;</span>
            <span>{article.readTime} 分钟阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 font-label whitespace-nowrap">
            {article.tags[0]}
          </span>
          <span className="text-xs text-foreground-500">{article.readTime} 分钟阅读</span>
        </div>
        <h3 className="text-base font-heading font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-1.5 line-clamp-2">{article.summary}</p>
        <p className="text-xs text-foreground-500 mt-3">{article.publishDate}</p>
      </div>
    </Link>
  );
}